import React from 'react';
import { Link } from 'react-router-dom';
import { Bookmark, Info, Lock, MessageCircle, Minus, Plus, ShoppingBag, Trash2 } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { formatPrice, getOption, getPricing } from '../data/products';
import { INSTAGRAM_URL, MINIMUM_ORDER, PAYMENT_METHODS, buildShopifyCartUrl } from '../data/contact';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
export function Panier() {
  const {
    t,
    language
  } = useLanguage();
  const {
    lines,
    saved,
    setQuantity,
    removeItem,
    addItem,
    removeSaved
  } = useCart();
  const resolveName = (id: string) => t.products.items.find(item => item.id === id)?.name ?? id;
  const detailedLines = lines.map(line => {
    const pricing = getPricing(line.id);
    const option = getOption(line.id, line.optionId);
    const price = option?.price ?? pricing?.price ?? 0;
    return {
      ...line,
      name: resolveName(line.id),
      unit: option?.label[language] ?? pricing?.unit[language] ?? '',
      image: option?.image ?? pricing?.image,
      variantId: option?.variantId ?? pricing?.variantId,
      price,
      total: price * line.quantity
    };
  });
  const savedLines = saved.map(line => {
    const pricing = getPricing(line.id);
    const option = getOption(line.id, line.optionId);
    return {
      ...line,
      name: resolveName(line.id),
      unit: option?.label[language] ?? pricing?.unit[language] ?? '',
      image: option?.image ?? pricing?.image,
      price: option?.price ?? pricing?.price ?? 0
    };
  });
  const subtotal = detailedLines.reduce((sum, line) => sum + line.total, 0);
  const missing = Math.max(0, MINIMUM_ORDER - subtotal);
  const meetsMinimum = missing === 0;
  const shopifyUrl = buildShopifyCartUrl(detailedLines);
  return <>
      <PageHeader eyebrow={t.cart.eyebrow} titleLead={t.cart.titleLead} titleAccent={t.cart.titleAccent} data-id="element-388" data-mp="2pgklt4vz5b98" />

      <section className="bg-creme py-20 md:py-28" data-id="element-389" data-mp="3f5rp97g898m5">
        <div className="mx-auto max-w-5xl px-6 md:px-12" data-id="element-390" data-mp="3f72rv3xxsabo">
          {detailedLines.length === 0 ? <div className="border border-caramel/25 bg-creme-dark px-8 py-20 text-center" data-id="element-391" data-mp="3o5eeyvzlgpzd">
              <ShoppingBag size={32} strokeWidth={1.25} className="mx-auto mb-6 text-caramel" aria-hidden="true" data-id="element-392" data-mp="3o7r4bu7om2mg" />
              <p className="mb-8 font-serif text-xl italic text-cacao-800" data-id="element-393" data-mp="1w3nvluk1c4f">
                {t.cart.empty}
              </p>
              <Link to="/#creations" className="inline-block bg-cacao-900 px-8 py-4 font-sans text-xs uppercase tracking-[0.16em] text-creme transition-colors duration-300 hover:bg-caramel" data-id="element-394" data-mp="aqrw9kvff9xg">
                {t.cart.emptyCta}
              </Link>
            </div> : <>
              <ul className="border-t border-caramel/25" data-id="element-395" data-mp="pok6bxi7f649">
                {detailedLines.map(line => <li key={line.key} className="flex flex-col gap-5 border-b border-caramel/25 py-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-396" data-mp="2a7e7f6tcnkjz">
                    <div className="flex min-w-0 items-center gap-5" data-id="element-397" data-mp="207vpevx028uk">
                      {line.image && <img src={line.image} alt={line.name} className="h-20 w-20 shrink-0 object-cover" data-id="element-398" data-mp="1vawzkpkdco26" />}
                      <div className="min-w-0" data-id="element-399" data-mp="2j5zn8u7fslh2">
                        <h2 className="font-serif text-lg text-cacao-900" data-id="element-400" data-mp="2j4onr6l0xzvr">
                          {line.name}
                        </h2>
                        <p className="mt-1 font-sans text-sm font-light text-cacao-700" data-id="element-401" data-mp="3bmiuozyqfn4o">
                          {formatPrice(line.price)} {t.cart.each} · {line.unit}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-5" data-id="element-402" data-mp="2rchotqrgfmij">
                      <div className="flex items-center border border-caramel/40" data-id="element-403" data-mp="2wdfe0navtgtj">
                        <button type="button" onClick={() => setQuantity(line.key, line.quantity - 1)} aria-label={`${t.cart.quantity} -`} className="px-3 py-2 text-cacao-800 transition-colors hover:text-caramel" data-id="element-404" data-mp="2hg4waw7nbepd">
                          <Minus size={14} strokeWidth={2} data-id="element-405" data-mp="30f0pq5bojp25" />
                        </button>
                        <span className="min-w-8 text-center font-sans text-sm text-cacao-900" data-id="element-406" data-mp="35dk9bz2sworc">
                          {line.quantity}
                        </span>
                        <button type="button" onClick={() => setQuantity(line.key, line.quantity + 1)} aria-label={`${t.cart.quantity} +`} className="px-3 py-2 text-cacao-800 transition-colors hover:text-caramel" data-id="element-407" data-mp="3t1bkv0gioxrk">
                          <Plus size={14} strokeWidth={2} data-id="element-408" data-mp="1w71u69kikblb" />
                        </button>
                      </div>

                      <p className="min-w-20 text-right font-serif text-lg text-caramel" data-id="element-409" data-mp="13ga31qib2d4u">
                        {formatPrice(line.total)}
                      </p>

                      <button type="button" onClick={() => removeItem(line.key)} aria-label={t.cart.remove} className="text-cacao-700 transition-colors hover:text-caramel" data-id="element-410" data-mp="yj1i5i5rvuzw">
                        <Trash2 size={17} strokeWidth={1.5} data-id="element-411" data-mp="2nuk5u59ncd2q" />
                      </button>
                    </div>
                  </li>)}
              </ul>

              <div className="mt-10 flex items-center justify-between" data-id="element-412" data-mp="1gikrfe6spezq">
                <p className="font-sans text-xs uppercase tracking-[0.18em] text-cacao-800" data-id="element-413" data-mp="1ligw7fnbhagc">
                  {t.cart.subtotal}
                </p>
                <p className="font-serif text-3xl text-cacao-900" data-id="element-414" data-mp="1qibljerg1rza">
                  {formatPrice(subtotal)}
                </p>
              </div>

              <p className={`mt-4 border p-4 text-center font-sans text-sm ${meetsMinimum ? 'border-caramel/40 bg-creme-dark text-cacao-800' : 'border-caramel bg-caramel/10 text-cacao-900'}`} data-id="element-415" data-mp="xtehafk2hvpj">
                {meetsMinimum ? t.cart.minimumMet : t.cart.minimumWarning.replace('{amount}', String(Math.round(missing)))}
              </p>

              <div className="mt-8 space-y-4" data-id="element-416" data-mp="j2p1ezvnvsfc">
                <div className="flex items-start gap-3 border border-caramel/30 bg-creme-dark p-5" data-id="element-417" data-mp="bkh5fhrn8sb6">
                  <Info size={18} className="mt-0.5 shrink-0 text-caramel" aria-hidden="true" data-id="element-418" data-mp="6lw4th8hc6m7" />
                  <p className="font-sans text-sm font-light leading-relaxed text-cacao-800" data-id="element-419" data-mp="2m5e1diydqho">
                    {t.cart.notice}
                  </p>
                </div>
                <div className="flex items-start gap-3 border border-caramel/30 bg-creme-dark p-5" data-id="element-420" data-mp="3ostz4sgr2wwi">
                  <Info size={18} className="mt-0.5 shrink-0 text-caramel" aria-hidden="true" data-id="element-421" data-mp="3jyzk42c3b63r" />
                  <p className="font-sans text-sm font-light leading-relaxed text-cacao-800" data-id="element-422" data-mp="3b0vw8zdmtx8s">
                    {t.cart.deliveryNote}
                  </p>
                </div>
              </div>

              <div className="mt-10 flex flex-col gap-4 sm:flex-row" data-id="element-423" data-mp="312pvil3kax5i">
                <a href={shopifyUrl} target="_blank" rel="noopener noreferrer" aria-disabled={!meetsMinimum} onClick={event => {
              if (!meetsMinimum) event.preventDefault();
            }} className={`flex flex-1 items-center justify-center gap-3 px-8 py-4 font-sans text-xs uppercase tracking-[0.16em] transition-colors duration-300 ${meetsMinimum ? 'bg-cacao-900 text-creme hover:bg-caramel' : 'cursor-not-allowed bg-cacao-700/40 text-creme/70'}`} data-id="element-424" data-mp="st8bamgc6qhl">
                  <ShoppingBag size={16} strokeWidth={1.5} aria-hidden="true" data-id="element-425" data-mp="zzkrkvjwxn2" />
                  {t.cart.checkout}
                </a>
                <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="flex flex-1 items-center justify-center gap-3 border border-cacao-900 px-8 py-4 font-sans text-xs uppercase tracking-[0.16em] text-cacao-900 transition-colors duration-300 hover:bg-cacao-900 hover:text-creme" data-id="element-426" data-mp="3n8r0jx5g4lcy">
                  <MessageCircle size={16} strokeWidth={1.5} aria-hidden="true" data-id="element-427" data-mp="3il13gw5d97l4" />
                  {t.cart.orderByDm}
                </a>
              </div>

              <div className="mt-8 border border-caramel/30 bg-creme-dark p-6" data-id="element-428" data-mp="3rjlerc8lkezb">
                <p className="mb-5 flex items-start gap-3 font-sans text-sm font-light leading-relaxed text-cacao-800" data-id="element-429" data-mp="3ml1ncwb8iazk">
                  <Lock size={17} strokeWidth={1.5} className="mt-0.5 shrink-0 text-caramel" aria-hidden="true" data-id="element-430" data-mp="54tmbkbrjog2" />
                  {t.cart.securePayment}
                </p>
                <p className="mb-3 font-sans text-[10px] uppercase tracking-[0.18em] text-or" data-id="element-431" data-mp="a5pq7xw47alh">
                  {t.cart.paymentMethodsLabel}
                </p>
                <ul className="flex flex-wrap gap-2" data-id="element-432" data-mp="3h5gw233lpmac">
                  {PAYMENT_METHODS.map(method => <li key={method} className="border border-caramel/40 bg-creme px-3 py-1.5 font-sans text-[10px] uppercase tracking-[0.12em] text-cacao-800" data-id="element-433" data-mp="3c647ndx3075n">
                      {method}
                    </li>)}
                </ul>
              </div>

              <div className="mt-10 text-center" data-id="element-434" data-mp="3r3qvg3akkx8">
                <Link to="/#creations" className="font-sans text-xs uppercase tracking-[0.16em] text-cacao-800 underline decoration-caramel underline-offset-4 transition-colors hover:text-caramel" data-id="element-435" data-mp="3uwlmcel2x280">
                  {t.cart.continueShopping}
                </Link>
              </div>
            </>}

          <div className="mt-20 border-t border-caramel/25 pt-12" data-id="element-436" data-mp="2fetn4wdc0ukv">
            <h2 className="mb-8 flex items-center gap-3 font-serif text-2xl text-cacao-900" data-id="element-437" data-mp="uz3vozyf8437">
              <Bookmark size={20} strokeWidth={1.5} className="text-caramel" aria-hidden="true" data-id="element-438" data-mp="z0phjasxnspj" />
              {t.cart.savedTitle}
            </h2>

            {savedLines.length === 0 ? <p className="font-sans text-sm font-light text-cacao-700" data-id="element-439" data-mp="1dou26kbjqeay">
                {t.cart.savedEmpty}
              </p> : <ul className="grid gap-5 sm:grid-cols-2" data-id="element-440" data-mp="1imc4vsyyzxai">
                {savedLines.map(line => <li key={line.key} className="flex items-center gap-4 border border-caramel/25 bg-creme-dark p-4" data-id="element-441" data-mp="1mprxme7x5dhx">
                    {line.image && <img src={line.image} alt={line.name} className="h-16 w-16 shrink-0 object-cover" data-id="element-442" data-mp="3oipuqm8qas8o" />}
                    <div className="min-w-0 flex-1" data-id="element-443" data-mp="vm4swe0cw80m">
                      <p className="font-serif text-base text-cacao-900" data-id="element-444" data-mp="vng5927vao3c">
                        {line.name}
                      </p>
                      <p className="mb-2 font-sans text-xs font-light text-cacao-700" data-id="element-445" data-mp="19jjndx5gv5zg">
                        {formatPrice(line.price)} · {line.unit}
                      </p>
                      <div className="flex flex-wrap gap-3" data-id="element-446" data-mp="1ji96ft93mwha">
                        <button type="button" onClick={() => {
                    addItem(line.id, line.optionId);
                    removeSaved(line.key);
                  }} className="font-sans text-[10px] uppercase tracking-[0.14em] text-caramel underline decoration-caramel underline-offset-4 transition-colors hover:text-cacao-900" data-id="element-447" data-mp="1elsgctica81k">
                          {t.cart.moveToCart}
                        </button>
                        <button type="button" onClick={() => removeSaved(line.key)} className="font-sans text-[10px] uppercase tracking-[0.14em] text-cacao-700 transition-colors hover:text-caramel" data-id="element-448" data-mp="2gv7f5oxk21ov">
                          {t.cart.remove}
                        </button>
                      </div>
                    </div>
                  </li>)}
              </ul>}
          </div>
        </div>
      </section>
    </>;
}